/**
 * REST client plus token storage. The token lives in localStorage so a refresh
 * or a dropped phone connection comes back as the same player with the same
 * score, rather than burning a fresh username.
 */

/**
 * Where the backend lives. Both the dev server (bunfig `env = "BUN_PUBLIC_*"`)
 * and the production build (`define` in build.ts) replace this expression with
 * a string literal, so nothing here survives into the browser.
 *
 * Do NOT guard this with `typeof process !== "undefined"`. `process` does not
 * exist in a browser, so that guard is false at runtime and every deployed
 * build silently falls back to localhost — which is exactly the bug that made
 * the deployed site unable to reach anything.
 */
const API = (process.env.BUN_PUBLIC_API_URL || "http://localhost:3000").replace(/\/+$/, "");

/**
 * Optional seat namespace. `?seat=2` gives that tab its own token slot, which
 * is the only way to be two different people in one browser. The value sticks
 * to the tab (sessionStorage) so it survives navigation inside the app.
 */
const SEAT = (() => {
  if (typeof window === "undefined") return "";
  const fromUrl = new URLSearchParams(window.location.search).get("seat");
  if (fromUrl) sessionStorage.setItem("dwordle.seat", fromUrl);
  return sessionStorage.getItem("dwordle.seat") ?? "";
})();

const TOKEN_KEY = SEAT ? `dwordle.token.${SEAT}` : "dwordle.token";
const ROOM_KEY = SEAT ? `dwordle.room.${SEAT}` : "dwordle.room";

export type Identity = { username: string; isAdmin: boolean };

export const store = {
  token: () => localStorage.getItem(TOKEN_KEY),
  setToken: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clearToken: () => localStorage.removeItem(TOKEN_KEY),
  room: () => localStorage.getItem(ROOM_KEY),
  setRoom: (code: string) => localStorage.setItem(ROOM_KEY, code),
  clearRoom: () => localStorage.removeItem(ROOM_KEY),
};

export class ApiError extends Error {}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = store.token();
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(body.error ?? `Request failed (${res.status})`);
  return body as T;
}

export async function signIn(username: string, password?: string): Promise<Identity> {
  const body = await request<{ token: string } & Identity>("/auth", {
    method: "POST",
    body: JSON.stringify(password ? { username, password } : { username }),
  });
  store.setToken(body.token);
  return { username: body.username, isAdmin: body.isAdmin };
}

export async function restoreSession(): Promise<Identity | null> {
  if (!store.token()) return null;
  try {
    return await request<Identity>("/session");
  } catch {
    store.clearToken();
    return null;
  }
}

export async function createRoom(): Promise<string> {
  const body = await request<{ roomId: string }>("/create", { method: "POST" });
  return body.roomId;
}

export async function joinRoom(code: string): Promise<{ roomid: string; reconnect: boolean }> {
  return request(`/join/${encodeURIComponent(code.toUpperCase())}`);
}

export function socketUrl(code: string): string {
  const base = API.replace(/^http/, "ws");
  return `${base}/ws?token=${encodeURIComponent(store.token() ?? "")}&room=${encodeURIComponent(
    code.toUpperCase(),
  )}`;
}
